import styled from 'styled-components'

// globalStyles resets `outline: 0` on everything, so every control in this
// modal has to restore its own focus ring - it is a payment dialog, and the
// close button is the only way out of it.
const focusRing = `
  &:focus-visible {
    outline: 2px solid #3D6666;
    outline-offset: 2px;
  }
`

/* $compact is the 'new-tab' mode: there is no embedded portal to give room to,
   so the dialog sizes to its content instead of taking the whole viewport. */
export const Container = styled.div<{ $compact?: boolean }>`
  width: ${props => props.$compact ? 'min(560px, 88vw)' : '90vw'};
  ${props => props.$compact ? '' : 'height: 90vh;'}
  display: flex;
  flex-direction: column;
  gap: ${props => props.$compact ? '8px' : '24px'};
`

export const ModalHeader = styled.header`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 24px;
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const HelperText = styled.span`
  color: #65666A;
  font-weight: 400;
  font-size: 14px;
  line-height: 21px;
`

export const OpenInNewTabLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0;
  /* Teal, not the primary orange: this is a secondary way out, and it must not
     read as the main action on a screen about to take card details. */
  color: #3D6666;
  font-weight: 900;
  font-size: 14px;
  line-height: 21px;
  text-decoration: underline;

  ${focusRing}

  svg {
    min-width: 16px;
    min-height: 16px;
  }
`

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  background: inherit;
  border: none;
  /* Negative margin keeps the icon visually aligned while giving the modal's
     only exit a 40px hit target. */
  padding: 8px;
  margin: -8px;
  color: #353744;

  ${focusRing}

  svg {
    width: 24px;
    height: 24px;
  }
`

export const InvoiceCloudFrame = styled.iframe`
  width: 100%;
  /* flex:1 rather than height:100% - the latter resolves against the container's
     full 90vh and pushes the frame past the bottom of the modal. */
  /* basis auto, not 0: in compact mode the container has no fixed height */
  flex: 1 1 auto;
  min-height: 0;
  border: none;
`

export const LoaderContainer = styled.div`
  width: 100%;
  /* basis auto, not 0: in compact mode the container has no fixed height */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  img {
    width: 64px;
    height: 64px;
  }
`

export const LoaderMessage = styled.p`
  max-width: 520px;
  color: #65666A;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`

export const ReadyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 24px 24px;
  text-align: center;

  h3 {
    color: #353744;
    font-weight: 900;
    font-size: 20px;
    line-height: 27px;
  }

  p {
    max-width: 460px;
    color: #65666A;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  }
`

/* Visually the primary button, but a real anchor - see the comment at its use
   site in index.tsx for why this is not a window.open call. */
export const PrimaryLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 28px;
  background: #E1712A;
  border: 2px solid #E1712A;
  color: #FFFFFF;
  font-weight: 900;
  font-size: 14px;
  line-height: 21px;
  white-space: nowrap;
  text-decoration: none;

  ${focusRing}

  svg {
    width: 18px;
    height: 18px;
  }
`

export const ErrorContainer = styled.div`
  width: 100%;
  /* basis auto, not 0: in compact mode the container has no fixed height */
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0 24px;
  text-align: center;

  > svg {
    width: 32px;
    height: 32px;
    color: #E84242;
  }

  h3 {
    /* The icon carries the alarm; a full red heading overstates a load failure
       the customer can usually just retry. */
    color: #353744;
    font-weight: 900;
    font-size: 20px;
    line-height: 27px;
  }

  p {
    max-width: 520px;
    color: #65666A;
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
  }
`

export const ContactLink = styled.a`
  color: #3D6666;
  font-weight: 900;
  text-decoration: underline;
  white-space: nowrap;

  ${focusRing}
`

export const ErrorActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 12px;

  /* Scoped to this modal: the shared OrangeButton has no focus style, and
     changing it would reach the whole portal. */
  button {
    ${focusRing}
  }
`
